import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Snackbar, Stack } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { FieldMapping } from '../../models/configuration/xpathFieldMap/IXpathFieldMap';
import { AlertSeverity } from '../../constants/AlertTypes';
import { ValidateXPathXpression, XpathFieldIdPrefix } from '../../constants/XpathConstants';
import { DtDialog } from '../ui/dialog';
import DtForm, { DtFormHandles } from "../ui/form/DtForm";
import { FormField } from '../ui/form';

interface XPathGridItemAddDialogProps {
    open: boolean;
    closeFxtn: () => void;
    title?: string | undefined;
    updateFxtn: (item: FieldMapping) => void;
    existingItems?: FieldMapping[];
}

const XPathGridItemAddDialog: React.FC<XPathGridItemAddDialogProps> = ({
    open, closeFxtn, title, updateFxtn, existingItems = []
}) => {
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const formRef = useRef<DtFormHandles>(null);

    // Form fields definition
    const fields: FormField[] = [
        {
            id: "Name",
            label: "Description",
            type: "text",
            required: true,
            grid: { xs: 12 },
            validate(value, formState) {
                if (!value || value.trim() === "") {
                return "Description is required.";
            }
            },
        },
        {
            id: "EncompassFieldId",
            label: "Output Encompass Path",
            type: "text",
            required: true,
            grid: { xs: 12 },
            validate: (value: string) => {
                if (!value || value.trim() === "") {
                   return "Output Encompass Path is required.";
                }
                if (!value?.toLocaleUpperCase().startsWith(XpathFieldIdPrefix)) {
                    return `Encompass Field Id must start with ${XpathFieldIdPrefix}`;
                }
                return undefined;
            }
        },
        {
            id: "Xpath",
            label: "Input XPath",
            type: "textarea",
            required: true,
            grid: { xs: 12 },
            validate: (value: string) => {
                if (!value || value.trim() === "") {
                return "XPath is required.";
                }
                const xapthExpression = ValidateXPathXpression(value);
                return xapthExpression?.isValid ? undefined : xapthExpression?.message;
            }
        }
    ];

    // Form submit handler
    const handleFormSubmit = (data: Record<string, any>) => {
        // Check for duplicate EncompassFieldId
        const existingItem = existingItems.find(item =>
            item.EncompassFieldId?.toLocaleUpperCase() === data.EncompassFieldId?.toLocaleUpperCase()
        );
        if (existingItem) {
            setAlertSeverity('error');
            setAlertMessage(`Pushback fields with the given Output Encompass Path '${data.EncompassFieldId}' already exists`);
            setAlertOpen(true);
            return;
        }
        updateFxtn(data as FieldMapping);
        closeFxtn();
    };

    const handleSnackBarClose = () => setAlertOpen(false);

    const handleClear = () => {
        formRef.current?.reset();
    };

    const handleSaveAndClose = () => {
        formRef.current?.submit();
    };

    useEffect(() => {
        if (open) {
            handleClear();
            setAlertOpen(false);
            setAlertMessage('');
            setAlertSeverity('info');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <DtDialog
            title={title ?? "Title"}
            open={open}
            onClose={closeFxtn}
            actions={
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button size='small' variant="text" onClick={handleClear}>Clear</Button>
                    <Button
                        size='small'
                        variant="contained"
                        onClick={handleSaveAndClose}
                    >Save & Close</Button>
                </Stack>
            }
        >
                <DtForm
                    ref={formRef}
                    fields={fields}
                    onSubmit={handleFormSubmit}
                    showSubmit={false}
                    showReset={false}
                    labelPosition="ontop"
                />
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={alertOpen}
                    autoHideDuration={4000}
                    onClose={handleSnackBarClose}
                >
                    <MuiAlert
                        onClose={handleSnackBarClose}
                        severity={alertSeverity}
                        sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                        {alertMessage}
                    </MuiAlert>
                </Snackbar>
        </DtDialog>
    );
};

export default XPathGridItemAddDialog;